import React, { useEffect, useState, useCallback, useRef } from 'react';
import _debounce from 'lodash/debounce';

import API from 'apis/API';
import useCheckPermissions from 'utils/useCheckPermissions';
import { handledErrorMessage } from 'utils/apiError';

const FolderContext = React.createContext(null);

export const FolderContextProvider = ({ children }) => {
	const defaultFilter = {
		search: '',
		type: '',
		sort: ''
	};

	const [foldersList, setFoldersList] = useState([]);
	const [filesList, setFilesList] = useState([]);
	const [folderId, setFolderId] = useState(null);
	const [folder, setFolder] = useState(null);
	const lastFetchKeyRef = useRef(null);
	const fetchFoldersAndFilesRef = useRef(() => {});
	const [filters, setFilters] = useState(defaultFilter);
	const [filterStatus, setFilterStatus] = useState(false);
	const [foldersListWithoutChildren, setFoldersListWithoutChildren] = useState(null);
	const [foldersForBreadcrumbs, setFoldersForBreadcrumbs] = useState([]);
	const [roles, setRoles] = useState([]);
	const [users, setUsers] = useState([]);
	const [expanded, setExpanded] = useState([]);
	const [loadError, setLoadError] = useState(null);

	const canShareFolders = useCheckPermissions('can_share_folders', true);

	const loadFilteredFolders = (search) => {
		API.folders.index({ search }).then(res => {
			if (res.data?.code === 200) {
				setFoldersList(res.data.data);
			}
		});
	};

	// Opening a folder fetches from the click and again from the folderId effect.
	// Skip that second identical listing so the folder preview is logged once.
	const fetchFoldersAndFiles = (nextFolderId, nextFilters, force = false) => {
		const key = [
			nextFolderId ?? '',
			nextFilters?.search || '',
			nextFilters?.type || '',
			nextFilters?.sort || '',
		].join('\0');

		if (!force && lastFetchKeyRef.current === key) return;
		lastFetchKeyRef.current = key;
		setFolderId(nextFolderId);
		setLoadError(null);

		API.folders.fetchSubfoldersAndFiles(nextFolderId, nextFilters).then(res => {
			if (res?.data?.code === 200) {
				setFilesList(res.data.data);
				setFilterStatus(res.data.search);
			}
		}).catch(err => setLoadError(handledErrorMessage(err)));
	};

	fetchFoldersAndFilesRef.current = fetchFoldersAndFiles;

	// A failed listing must not be reported as an empty folder, so the retry keeps
	// the current folder and filters.
	const retryFetchFoldersAndFiles = () => fetchFoldersAndFiles(folderId, filters, true);

	// create folders array without children
	const arrangeFolders = (folders, result = {}) => {
		folders?.forEach(folder => {
			const { children, ...other } = folder;
			result[folder.id] = other;
			if (children) {
				arrangeFolders(children, result);
			}
		});

		return result;
	};

	// set target folder to parent one to generate breadcrumbs
	const setBreadcrumbsArr = (obj) => {
		let res = [];
		if (obj) {
			for (; ;) {
				res.push(obj);
				if (obj?.parent_id === null) break;
				obj = foldersListWithoutChildren[obj?.parent_id];
			}
			res.reverse();
		}

		setFoldersForBreadcrumbs(res);
	};

	useEffect(() => {
		loadFilteredFolders();
		fetchFoldersAndFiles();

		if (canShareFolders) {
			API.roles.all().then(res => {
				setRoles(res?.data?.data);
			});
			API.users.all().then(res => {
				setUsers(res?.data?.data);
			});
		}
	}, []);

	useEffect(() => {
		const list = arrangeFolders(foldersList);

		setFoldersListWithoutChildren(list);
	}, [foldersList]);

	const debouncedChangeHandler = useCallback(_debounce((nextFolderId, nextFilters) => {
		fetchFoldersAndFilesRef.current(nextFolderId, nextFilters);
	}, 500), []);

	return (
		<FolderContext.Provider value={{
			foldersList,
			filesList,
			folderId,
			filters,
			filterStatus,
			foldersListWithoutChildren,
			foldersForBreadcrumbs,
			roles,
			users,
			expanded,
			loadError,
			folder,
			setFolder,
			setExpanded,
			setBreadcrumbsArr,
			setFilters,
			setFolderId,
			setFilesList,
			loadFilteredFolders,
			fetchFoldersAndFiles,
			retryFetchFoldersAndFiles,
			debouncedChangeHandler,
		}}>
			{children}
		</FolderContext.Provider>
	);
};

export default FolderContext;
